<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { useQuery } from '@/composables/api'
import { useDialogsStore } from '@/stores/dialogsStore'

import BDialog from '@/components/base/BDialog/BDialog.vue'

import mdiClose from '~icons/mdi/close'

// Singleton media viewer (demand letters, original invoices, ticket attachments) — opens
// via dialogsStore.media; replaces @old media_dialog (ADA-4437 etapp 1). Renderer picked by
// content_type; without one the sandbox iframe fallback is Chrome-blocked, so senders
// default PDFs to application/pdf (legacy contract kept).
const { t } = useI18n()
const dialogsStore = useDialogsStore()
const model = computed({
  get: () => dialogsStore.media != null,
  set: (value) => {
    if (!value) dialogsStore.media = null
  },
})
const { mobile } = useDisplay()

const urlQuery = useQuery(
  '/media--url/{resource}/{resource_id}/{bucket_id}',
  computed(() => ({
    path: {
      resource: dialogsStore.media?.resource ?? '',
      resource_id: dialogsStore.media?.documentId ?? '',
      bucket_id: dialogsStore.media?.fileId ?? '',
    },
  })),
  { enabled: computed(() => dialogsStore.media != null) }
)
const url = computed(() => urlQuery.data.value?.url)

const contentType = computed(() => dialogsStore.media?.contentType)
const isImage = computed(() => contentType.value?.startsWith('image/') ?? false)
const isVideo = computed(() => contentType.value?.startsWith('video/') ?? false)
const isPdf = computed(() => contentType.value === 'application/pdf')
</script>

<template>
  <b-dialog
    v-model="model"
    :aria-label="t('common.document_viewer')"
    :fullscreen="mobile"
    hide-header
    hide-footer
    size="xxlarge"
    height="90vh"
  >
    <template v-if="url != null">
      <v-img
        v-if="isImage"
        :src="url"
        height="100%"
        max-height="100%"
        max-width="100%"
        width="100%"
      />
      <video
        v-else-if="isVideo"
        controls
      >
        <source
          :src="url"
          :type="contentType"
        />
      </video>
      <object
        v-else-if="isPdf && !mobile"
        :key="url"
        :data="url"
        :type="contentType"
      />
      <iframe
        v-else-if="isPdf"
        :src="url"
        :title="t('common.document_viewer')"
      />
      <iframe
        v-else
        :src="url"
        :title="t('common.document_viewer')"
        sandbox=""
      />
    </template>

    <v-skeleton-loader
      v-else
      type="image"
      height="100%"
    />

    <v-btn
      :icon="mdiClose"
      :aria-label="t('common.close')"
      position="absolute"
      location="bottom"
      size="x-large"
      color="secondary"
      class="mb-12"
      @click="model = false"
    />
  </b-dialog>
</template>

<style scoped>
iframe,
video,
object {
  /* display: block kills the inline-baseline gap that otherwise adds a few px of
     scrollable space under the viewer (double scrollbar). */
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
  border: none;
}
</style>
